import { Box, ListItemButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { NotificationItem } from '~/apis/notificationsApi';
import { formatNotificationDate } from './formatNotificationDate';

type NotificationListItemProps = {
  item: NotificationItem;
  onClick: () => void;
};

export function NotificationListItem({ item, onClick }: NotificationListItemProps) {
  const theme = useTheme();
  const isUnread = item.readAt == null;

  return (
    <ListItemButton
      onClick={onClick}
      alignItems="flex-start"
      sx={{
        py: 2,
        px: 2.5,
        gap: 1,
        borderLeft: isUnread ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        bgcolor: isUnread ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
        '&:hover': {
          bgcolor: isUnread
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.action.hover, 0.04),
        },
      }}
    >
      {isUnread && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            flexShrink: 0,
            mt: 0.75,
          }}
          aria-hidden
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0, pl: isUnread ? 0 : 1.25 }}>
        <Typography
          variant="subtitle2"
          fontWeight={isUnread ? 600 : 400}
          sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
        >
          {item.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
        >
          {item.body}
        </Typography>
        {item.metadata ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {item.metadata}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {formatNotificationDate(item.createdAt)}
        </Typography>
      </Box>
    </ListItemButton>
  );
}
